import { useState, useRef, useEffect } from 'react';
import type { DropdownProps } from '../../types';

export default function Dropdown({
  label,
  placeholder,
  value,
  onChange,
  options,
  error,
  helper,
  size = 'md',
  disabled = false,
  className = '',
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const selectedOption = options.find((opt) => opt.value === value);

  const containerClasses = [
    'dropdown-container',
    error && 'dropdown-container--error',
    disabled && 'dropdown-container--disabled',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const triggerClasses = [
    'dropdown__trigger',
    `dropdown__trigger--${size}`,
    isOpen && 'dropdown__trigger--open',
    !value && 'dropdown__trigger--placeholder',
  ]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  function handleTriggerClick() {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  }

  function handleOptionClick(optionValue: string, optionDisabled?: boolean) {
    if (optionDisabled) return;
    onChange(optionValue);
    setIsOpen(false);
    triggerRef.current?.focus();
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
        return;
      }
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightedIndex((prev) =>
          prev < options.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (highlightedIndex >= 0 && options[highlightedIndex]) {
          handleOptionClick(options[highlightedIndex].value, options[highlightedIndex].disabled);
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  }

  return (
    <div className={containerClasses} ref={containerRef}>
      {label && <label className="dropdown__label">{label}</label>}
      <div className="dropdown__wrapper">
        <button
          ref={triggerRef}
          type="button"
          className={triggerClasses}
          onClick={handleTriggerClick}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={isOpen}
        >
          <span className="dropdown__value">
            {selectedOption?.label || placeholder || 'Select...'}
          </span>
          <span className={`dropdown__icon ${isOpen ? 'dropdown__icon--open' : ''}`}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </span>
        </button>
        {isOpen && (
          <ul className="dropdown__menu" role="listbox">
            {options.map((option, index) => (
              <li
                key={option.value}
                className={[
                  'dropdown__option',
                  index === highlightedIndex && 'dropdown__option--highlighted',
                  option.value === value && 'dropdown__option--selected',
                  option.disabled && 'dropdown__option--disabled',
                ]
                  .filter(Boolean)
                  .join(' ')}
                onClick={() => handleOptionClick(option.value, option.disabled)}
                role="option"
                aria-selected={option.value === value}
                aria-disabled={option.disabled}
              >
                <span>{option.label}</span>
                {option.value === value && (
                  <span className="dropdown__check">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>
      {error && <span className="dropdown__error">{error}</span>}
      {helper && !error && <span className="dropdown__helper">{helper}</span>}
    </div>
  );
}
